import { _decorator, Component, WebView, Node, Camera, screen, game } from 'cc';
import Page from '../../common/scripts/Toolkit/Components/Page/Page';
import Delay from '../../common/scripts/Toolkit/Utils/Delay';
import { LobbyMessagePathName, SoundData } from '../../lobbyCommunicator/Data/Lobby.dto';
import DataType from '../../tableEngine/Script/Game/Data/Config/DataStructure';
import Lobby from '../System/Lobby';
import performanceConfig from './Data/Config/PerformanceConfig';
import NodeName from './Data/Config/SceneNodeName';
import PreloadView from './UI/PreloadView';

const { ccclass, property } = _decorator;

enum WebViewManagerEvent {
    OPEN_GAME = 'OPEN_GAME',
    GAME_WINDOW_INIT = 'GAME_WINDOW_INIT',
    GAME_START = 'GAME_START',
    CLOSE_GAME = 'CLOSE_GAME',

    HANDPAY_OPEN = 'HANDPAY_OPEN',
    HANDPAY_FINISH = 'HANDPAY_FINISH',
}

@ccclass('WebViewManager')
export default class WebViewManager extends Component {
    public static readonly EVENT_TYPE = WebViewManagerEvent;

    @property(Camera)
    public readonly camera: Camera = null;
    @property(Node)
    public readonly webViewRoot: Node = null;
    @property(Node)
    public readonly fakeLoadingView: Node = null;
    @property(PreloadView)
    public readonly fakePreloadView: PreloadView = null;
    @property(WebView)
    public readonly webView: WebView = null;

    private _isGamePlaying: boolean = false;
    private _isFirstOpen: boolean = true;
    private _lastGameID: string = '';
    private _lastGameType: DataType.Enums.PlayType = null;
    private _lastStartTime: number = 0;
    private _waitingForHandpay: boolean = false;

    public get isGamePlaying() {
        return this._isGamePlaying;
    }

    public get isWaitingForHandpay() {
        return this._waitingForHandpay;
    }

    public get lastGameID() {
        return this._lastGameID;
    }

    public onLoad() {
        if (Lobby.config.debug) {
            (window as any).wbManager = this;
        }
        Lobby.Instance.initCommunicatorEvents();
    }

    public async reEnterLastGame() {
        if (this._lastGameID === '' || this._lastGameType === null) {
            return;
        }
        const targetGame = Lobby.hall.gameList.find((g) => g.GameType === this._lastGameType);
        if (!targetGame) {
            return;
        }
        const url = await Promise.race([Lobby.hall.getGameUrl(targetGame.GameID, targetGame.GameType), Delay.resolve(10000).then(() => '')]);
        Lobby.hall.blockRequestGameUrl = false;
        if (url === '') {
            this.closeGame();
            return;
        }
        if (this.isGamePlaying) {
            this.closeGame();
        }
        this.openGame(url, targetGame.GameID, targetGame.GameType);
    }

    public openGame(gameUrl: string, gameID: string, gameType: DataType.Enums.PlayType) {
        if (this.isWaitingForHandpay) {
            return;
        }
        if (gameUrl === '') {
            return;
        }

        const startTime = Date.now();
        this._lastStartTime = startTime;
        this._lastGameID = gameID;
        this._lastGameType = gameType;
        Lobby.sharedAsset.lobbyView.active = false;
        screen.maxWindowSideLength = performanceConfig.lobby.loadingResolution;
        game.frameRate = performanceConfig.lobby.loadingFrameRate;
        Lobby.audio.pauseMusic();
        Lobby.audio.volumeOnlyPassFilters = [];

        Promise.race([Delay.event(WebViewManager.EVENT_TYPE.GAME_WINDOW_INIT, this), Delay.resolve(10000)]).then((result) => {
            if (result !== WebViewManager.EVENT_TYPE.GAME_WINDOW_INIT) {
                this.loadGameFinish();
                this.closeGame();
            }
        });

        Lobby.communicator.once(LobbyMessagePathName.Open, () => {
            if (this._lastStartTime !== startTime) {
                return;
            }
            if (!this.fakeLoadingView.active) {
                this.webView.url = '';
                return;
            }
            this.emit(WebViewManager.EVENT_TYPE.GAME_WINDOW_INIT);
            this._isGamePlaying = true;

            // const jackpotData: { [key: number]: { value: number; upperLimit: number; multiplier?: number; hint?: boolean } } = {};
            // Lobby.jackpot.poolList[0].forEach((jackpot) => {
            //     jackpotData[jackpot.Level] = {
            //         value: jackpot.Value,
            //         upperLimit: jackpot.TriggerUpperLimit,
            //     };
            // });
            const audioData: SoundData = {
                musicMute: Lobby.audio.musicMute,
                effectMute: Lobby.audio.effectMute,
                musicVolume: Lobby.audio.rawMusicVolume,
                effectVolume: Lobby.audio.rawEffectVolume,
            };
            Lobby.communicator.send(LobbyMessagePathName.Open, {
                // 大廳同步音量設定給遊戲
                config: {
                    audio: audioData,
                },
                performance: {
                    defaultFrameRate: performanceConfig.game.defaultFrameRate,
                    defaultResolution: performanceConfig.game.defaultResolution,
                    loadingFrameRate: performanceConfig.game.loadingFrameRate,
                    loadingResolution: performanceConfig.game.loadingResolution,
                },
            });
            // 大廳同步語言給遊戲 (但應該是用網址傳送?)
            Lobby.communicator.syncGameLanguage(Lobby.i18n.currentLanguage);
            Lobby.communicator.syncGameSound(audioData);
        });
        Lobby.communicator.once(LobbyMessagePathName.Start, () => {
            this.loadGameFinish();
            this.camera.enabled = true;
            this.emit(WebViewManager.EVENT_TYPE.GAME_START);
            game.frameRate = performanceConfig.lobby.gamingFrameRate;
            screen.maxWindowSideLength = performanceConfig.lobby.gamingResolution;
        });

        this.loadGame(gameUrl);

        if (this._isFirstOpen) {
            Delay.resolve(0).then(() => {
                Lobby.Instance.initCommunicator();
            });
            this._isFirstOpen = false;
        }
        this.emit(WebViewManager.EVENT_TYPE.OPEN_GAME);
    }

    public async closeGame() {
        this._isGamePlaying = false;
        this.webView.url = '';
        this.handleGameView(true);
        const jackpotPage = Lobby.scene.tryGetNode(NodeName.JackpotPage)?.getComponent(Page);
        if (!jackpotPage?.isVisible) {
            Lobby.audio.resumeMusic();
        }
        Lobby.audio.clearVolumeOnlyPassFilters();
        game.frameRate = performanceConfig.lobby.defaultFrameRate;
        screen.maxWindowSideLength = performanceConfig.lobby.defaultResolution;
        // Lobby.sharedAsset.outerLobbyUIController.forceUpdateGameList();
        // if (Lobby.user.creditLimitTrigger) {
        //     Lobby.error.handle(ErrorName.CreditLimit);
        // }
        this.emit(WebViewManager.EVENT_TYPE.CLOSE_GAME);
        await Lobby.Instance.relogin();
    }

    public handleGameView(hideGameView: boolean) {
        // 顯示遊戲畫面
        if (hideGameView) {
            this.webView.node.active = false;
            this.webViewRoot.active = false;
            Lobby.sharedAsset.lobbyView.active = true;
            game.canvas.style.display = '';
            this.camera.enabled = true;
        } else {
            this.webView.node.active = true;
            this.webViewRoot.active = true;
            Lobby.sharedAsset.lobbyView.active = false;
            game.canvas.style.display = 'none';
            this.camera.enabled = false;
        }
    }

    public loadGame(gameUrl: string) {
        this.webView.url = gameUrl;
        this.webViewRoot.active = true;
        this.fakeLoadingView.active = true;
        this.webView.node.active = true;
        this._setWebViewZIndex(-1);

        this.fakePreloadView.resetProgress();
        Lobby.communicator.on(LobbyMessagePathName.Progress, this.updateLoadingProgress, this);
    }

    public loadGameFinish() {
        this.fakeLoadingView.active = false;
        this._setWebViewZIndex(0);
        game.canvas.style.display = 'none';
        this.camera.enabled = false;
        Lobby.communicator.off(LobbyMessagePathName.Progress, this.updateLoadingProgress, this);
    }

    protected updateLoadingProgress(data: { progress: number }) {
        this.fakePreloadView.updateProgress(data.progress);
    }

    private _setWebViewZIndex(zIndex: number) {
        const nativeWebView = this.webView?.nativeWebView;

        if (!nativeWebView) {
            return;
        }

        // Cocos WebView 通常會包在 parent wrapper 中
        const target = nativeWebView.parentElement ?? nativeWebView;
        target.style.zIndex = zIndex.toString();
    }
}
